import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { EDITOR_ROLES } from "@shared/core/types";
import { JOB_STATUS_LABELS, type ExtractionJob } from "@shared/modules/x-extraction";
import { useAuthStore } from "../../../core/stores/auth";
import { useToastStore } from "../../../core/stores/toast";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Button } from "../../../core/components/ui/button";
import { Card } from "../../../core/components/ui/card";
import { Badge } from "../../../core/components/ui/badge";
import { Field, Input } from "../../../core/components/ui/input";
import { Icon } from "../../../core/components/ui/icon";
import { formatDateTime } from "../../../core/lib/format";
import { allowedFor } from "../../../core/modules";
import { moduleEnabled } from "../../../core/blueprint";
import { engineState, importCvs, listJobs, requeueJob, type EngineState } from "../lib/api";

const VARIANT: Record<ExtractionJob["status"], "neutral" | "primary" | "success" | "warning" | "danger" | "info"> = {
  en_attente: "neutral",
  en_cours: "primary",
  a_valider: "warning",
  applique: "success",
  rejete: "neutral",
  echec: "danger",
};

export function ImportsPage() {
  const user = useAuthStore((s) => s.user);
  const push = useToastStore((s) => s.push);
  const [state, setState] = useState<EngineState | null>(null);
  const [jobs, setJobs] = useState<ExtractionJob[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const canEdit = allowedFor({ roles: EDITOR_ROLES }, user?.role);

  const load = useCallback(async () => {
    try {
      const [s, j] = await Promise.all([engineState(), listJobs()]);
      setState(s);
      setJobs(j);
    } catch {
      push("danger", "Impossible de charger les importations");
    }
  }, [push]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 10_000);
    return () => clearInterval(t);
  }, [load]);

  const upload = async () => {
    if (!files.length) return;
    setBusy(true);
    try {
      const r = await importCvs(files);
      if (r.created.length) push("success", `${r.created.length} CV importé(s) : lecture en cours`);
      for (const rej of r.rejected) push("warning", `${rej.name} : format non accepté`);
      setFiles([]);
      await load();
    } catch {
      push("danger", "Importation impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <PageHeader title="Importer des CV" subtitle="Dépôt à l'unité ou par lot ; lecture et proposition de fiche sur le serveur, validation par une personne." />

      <div className="importations">
        {canEdit && moduleEnabled("x-documents") ? (
          <Card title="Déposer des CV" subtitle="PDF, Word, numérisations ou photos. Chaque CV devient une proposition de dossier à valider.">
            <Field label="Fichiers">
              <Input type="file" multiple accept=".pdf,.docx,image/*" onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
            </Field>
            <div className="importations__actions">
              <Button variant="primary" disabled={busy || files.length === 0} onClick={upload} icon={<Icon name="sparkles" size={16} />}>
                Importer {files.length ? `(${files.length})` : ""}
              </Button>
            </div>
          </Card>
        ) : null}

        <Card title="Moteur de lecture" subtitle="Tout s'exécute sur le serveur ; aucune donnée ne part vers une IA externe.">
          {state ? (
            <dl className="kv">
              <dt>Modèle local</dt>
              <dd>
                <code>{state.ollama.model}</code>{" "}
                {state.ollama.available ? <Badge variant="success">disponible</Badge> : <Badge variant="warning">indisponible — identité seule</Badge>}
              </dd>
              <dt>Lecture automatique des CV</dt>
              <dd>{state.autoCv ? "Oui" : "À la demande"}</dd>
              <dt>File</dt>
              <dd>
                {Object.entries(state.queue).length
                  ? Object.entries(state.queue)
                      .map(([k, n]) => `${JOB_STATUS_LABELS[k as ExtractionJob["status"]] ?? k} : ${n}`)
                      .join(" · ")
                  : "vide"}
              </dd>
            </dl>
          ) : (
            <p className="muted">Chargement…</p>
          )}
        </Card>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th>Fichier</th>
            <th>Statut</th>
            <th className="table__col--wide">Dossier</th>
            <th className="table__col--wide">Déposé</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => (
            <tr key={j.id}>
              <td>{j.originalName ?? j.documentId}</td>
              <td>
                <Badge variant={VARIANT[j.status]}>{JOB_STATUS_LABELS[j.status]}</Badge>
                {j.error ? <span className="muted"> · {j.error}</span> : null}
              </td>
              <td className="table__col--wide">{j.candidateId ? <Link to={`/candidats/${j.candidateId}`}>voir le dossier</Link> : <span className="muted">nouveau dossier</span>}</td>
              <td className="table__col--wide">{formatDateTime(j.createdAt, { dateStyle: "short", timeStyle: "short" })}</td>
              <td className="table__actions">
                {j.status === "a_valider" ? <Link to={`/importations/${j.id}`}>Valider</Link> : null}
                {j.status === "echec" && canEdit ? (
                  <Button variant="ghost" size="sm" onClick={async () => { await requeueJob(j.id); await load(); }}>
                    Relancer
                  </Button>
                ) : null}
              </td>
            </tr>
          ))}
          {jobs.length === 0 ? (
            <tr>
              <td colSpan={5} className="table__empty">
                Aucune importation.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
